import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { buildApiUrl } from '../services/api';
import { SavedTemplateCard } from './SavedTemplateCard';

interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  messageType: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO';
  content: string;
  active: boolean;
  tags: string[];
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MessageTemplatesPanelProps {}

interface StarterTemplateDefinition {
  name: string;
  description: string;
  content: string;
}

interface TemplateCardModel {
  id: string;
  name: string;
  description: string;
  content: string;
  active: boolean;
  persisted: boolean;
}

const TEMPLATE_CATEGORY = 'Ações de Envio';

const normalizeTemplateName = (value: string) => value.trim().toLocaleLowerCase('pt-BR');

const STARTER_TEMPLATES: StarterTemplateDefinition[] = [
  {
    name: 'Apresentação Inicial',
    description: 'Mensagem usada para primeiro contato com o contato.',
    content: `Olá, {{nome}}! Tudo bem?

Meu nome é [SEU NOME] e sou Corretor de Consórcio/Seguro.
Estou entrando em contato para me apresentar e também me colocar à disposição para ajudar você a encontrar as melhores opções de proteção, segurança financeira e planejamento para seus objetivos.

Hoje existem oportunidades muito interessantes em seguros e consórcios que podem fazer sentido para o seu momento.

Se quiser, posso te enviar algumas informações sem compromisso 😊`
  },
  {
    name: 'Envio de Promoção',
    description: 'Mensagem promocional para campanhas.',
    content: `Olá, {{nome}}!

Passando para avisar que estamos com condições especiais em planos de Seguro/Consórcio por tempo limitado.

Como Corretor de Consórcio/Seguro, selecionei algumas opções com excelente custo-benefício que podem ajudar você a economizar e ainda garantir mais tranquilidade e segurança.

Se tiver interesse, posso te enviar uma simulação personalizada sem compromisso.`
  },
  {
    name: 'Novas Oportunidades',
    description: 'Aviso de novas ações e campanhas.',
    content: `Olá, {{nome}}! Tudo bem?

Quero te avisar que surgiram novas oportunidades e condições especiais em Seguro/Consórcio esta semana.

Como Corretor de Consórcio/Seguro, estou entrando em contato com alguns clientes para apresentar essas novidades antes do encerramento das campanhas.

Se quiser receber mais detalhes ou uma simulação atualizada, é só me chamar 😊`
  }
];

const createInitialForm = (template: StarterTemplateDefinition = STARTER_TEMPLATES[0]) => ({
  id: null as string | null,
  name: template.name,
  content: template.content,
  description: template.description,
  active: true
});

export function MessageTemplatesPanel({}: MessageTemplatesPanelProps) {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState(createInitialForm);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const activeTemplates = useMemo(
    () => templates.filter((template) => template.active),
    [templates]
  );

  const displayedTemplates = useMemo<TemplateCardModel[]>(() => {
    const templateMap = new Map(
      templates.map((template) => [normalizeTemplateName(template.name), template])
    );

    const starterCards = STARTER_TEMPLATES.map((starterTemplate, index) => {
      const persistedTemplate = templateMap.get(normalizeTemplateName(starterTemplate.name));

      return {
        id: persistedTemplate?.id || `starter-${index}`,
        name: persistedTemplate?.name || starterTemplate.name,
        description: persistedTemplate?.description || starterTemplate.description,
        content: persistedTemplate?.content || starterTemplate.content,
        active: persistedTemplate?.active ?? true,
        persisted: Boolean(persistedTemplate)
      };
    });

    const starterNames = new Set(
      STARTER_TEMPLATES.map((template) => normalizeTemplateName(template.name))
    );

    const customCards = templates
      .filter((template) => !starterNames.has(normalizeTemplateName(template.name)))
      .map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description || 'Template personalizado.',
        content: template.content,
        active: template.active,
        persisted: true
      }));

    return [...starterCards, ...customCards];
  }, [templates]);

  const authenticatedFetch = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(buildApiUrl(endpoint), {
      ...options,
      headers,
    });
  }, []);

  const fetchTemplates = useCallback(async (): Promise<MessageTemplate[]> => {
    const params = new URLSearchParams({
      mine: 'true',
      messageType: 'TEXT',
      category: TEMPLATE_CATEGORY
    });

    const response = await authenticatedFetch(`/templates?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();
    return payload.data || [];
  }, [authenticatedFetch]);

  const ensureStarterTemplates = useCallback(async (loadedTemplates: MessageTemplate[]) => {
    const existingNames = new Set(
      loadedTemplates.map((template) => template.name.trim().toLocaleLowerCase('pt-BR'))
    );

    const missingTemplates = STARTER_TEMPLATES.filter(
      (template) => !existingNames.has(template.name.trim().toLocaleLowerCase('pt-BR'))
    );

    if (missingTemplates.length === 0) {
      return false;
    }

    for (const template of missingTemplates) {
      const response = await authenticatedFetch('/templates', {
        method: 'POST',
        body: JSON.stringify({
          name: template.name,
          category: TEMPLATE_CATEGORY,
          messageType: 'TEXT',
          content: template.content,
          active: true,
          tags: ['campanha', 'acoes-envio'],
          description: template.description
        })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }
    }

    return true;
  }, [authenticatedFetch]);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const initialTemplates = await fetchTemplates();
      const createdMissingTemplates = await ensureStarterTemplates(initialTemplates);
      const finalTemplates = createdMissingTemplates ? await fetchTemplates() : initialTemplates;
      setTemplates(finalTemplates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      toast.error('Não foi possível carregar seus templates');
    } finally {
      setLoading(false);
    }
  }, [ensureStarterTemplates, fetchTemplates]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const resetForm = () => {
    setForm(createInitialForm());
    setIsFormOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error('Informe um nome para o template');
      return;
    }

    if (!form.content.trim()) {
      toast.error('Informe a mensagem do template');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        category: TEMPLATE_CATEGORY,
        messageType: 'TEXT',
        content: form.content,
        active: form.active,
        tags: ['campanha', 'acoes-envio'],
        description: form.description.trim()
      };

      const endpoint = form.id ? `/templates/${form.id}` : '/templates';
      const method = form.id ? 'PUT' : 'POST';

      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      toast.success(form.id ? 'Template atualizado com sucesso' : 'Template salvo com sucesso');
      resetForm();
      await loadTemplates();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    try {
      setDeletingId(templateId);
      const response = await authenticatedFetch(`/templates/${templateId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      toast.success('Template excluído');
      if (form.id === templateId) {
        resetForm();
      }
      await loadTemplates();
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir template');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditCard = (template: TemplateCardModel) => {
    setForm({
      id: template.persisted ? template.id : null,
      name: template.name,
      content: template.content,
      description: template.description || '',
      active: template.active
    });
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-white p-4">
        <h4 className="text-sm font-semibold text-blue-900">Mensagens modelo salvas</h4>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="text-sm font-semibold text-slate-950">Templates salvos</h5>
            <p className="text-xs text-slate-500">
              {activeTemplates.length} ativo(s) de {displayedTemplates.length} template(s) exibido(s)
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setForm(createInitialForm());
              setIsFormOpen(true);
            }}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Criar novo modelo
          </button>
        </div>

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            Carregando templates...
          </div>
        ) : displayedTemplates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
            Nenhum template salvo ainda.
          </div>
        ) : (
          <div className="no-scrollbar flex gap-4 overflow-x-auto pb-2 lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
            {displayedTemplates.map((template) => (
              <div
                key={template.id}
                className="min-w-[280px] flex-shrink-0 snap-start lg:min-w-0"
              >
                <SavedTemplateCard
                  title={template.name}
                  description={template.description}
                  preview={template.content}
                  active={template.active}
                  selected={form.id === template.id || (!form.id && form.name === template.name)}
                  onSelect={() => handleEditCard(template)}
                  onEdit={() => handleEditCard(template)}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {isFormOpen && (
      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h5 className="text-sm font-semibold text-slate-950">
              {form.id ? 'Editar template' : 'Novo template'}
            </h5>
            <p className="text-xs text-slate-500">
              Use variáveis como {'{{nome}}'}, {'{{email}}'} e {'{{telefone}}'}.
            </p>
          </div>
          {form.id ? (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Cancelar edição
            </button>
          ) : (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Fechar
            </button>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Nome do template
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Apresentação Inicial"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Descrição
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Mensagem usada para primeiro contato"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Texto modelo
          </label>
          <textarea
            value={form.content}
            onChange={(event) => setForm((prev) => ({ ...prev, content: event.target.value }))}
            rows={10}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Template ativo para uso rápido
        </label>

        <div className="flex flex-wrap gap-2">
          {['{{nome}}', '{{email}}', '{{telefone}}', '{{observacoes}}'].map((variable) => (
            <button
              key={variable}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, content: `${prev.content}${variable}` }))}
              className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200"
            >
              {variable}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? 'Salvando...' : form.id ? 'Atualizar template' : 'Salvar template'}
          </button>
          {form.id && (
            <button
              type="button"
              onClick={() => handleDelete(form.id!)}
              disabled={deletingId === form.id}
              className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {deletingId === form.id ? 'Excluindo...' : 'Excluir'}
            </button>
          )}
        </div>
      </form>
      )}
    </div>
  );
}
